import { useEffect, useRef, useState } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import { EInvoiceDraftEditor } from './einvoice-draft-editor'
import { EInvoicePublishConfirm } from './einvoice-publish-confirm'
import { EInvoiceObservationNotice } from './einvoice-observation-notice'
import { useOpenManualIssue } from './einvoice-manual-switch'
import type { RefreshEInvoiceDraft } from '@/lib/api/einvoice-api'
import { ApiError } from '@/lib/api'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { useEInvoiceDocument } from '@/hooks/use-einvoice-document'
import { orderHandedOver } from '@/lib/einvoice-handover'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

function statusLabel(status?: string | null) {
    switch (status) {
        case 'issued':
            return m.einvoice_issued()
        case 'issuing':
            return m.einvoice_badge_issuing()
        case 'rejected':
            return m.einvoice_badge_rejected()
        case 'pushed':
            return m.einvoice_pushed_title()
        case 'draft':
            return m.einvoice_draft_title()
        case 'failed':
            return m.einvoice_failed()
        default:
            return m.einvoice_processing()
    }
}

export function EInvoiceViewer({
    orderId,
    status,
    canMutate = false,
    onPublish,
    onRefreshDraft,
    initiallyOpen = false,
    onInitiallyOpened,
    orderCode,
    orderStatus,
    isQuickSale = false,
    triggerLabel,
    republish = false,
    triggerLink = false,
}: {
    orderId: string
    status: string
    canMutate?: boolean
    onPublish?: (token: string, notIssuedByHand?: boolean) => Promise<unknown>
    onRefreshDraft?: RefreshEInvoiceDraft
    initiallyOpen?: boolean
    /** Called once the viewer opened because of `initiallyOpen`, so the owner
     *  clears the flag and no later viewer pops open again (M-176). */
    onInitiallyOpened?: () => void
    orderCode?: string
    orderStatus?: string
    /** Đơn "Bán ngay": chốt đơn là được phát hành (M-132). */
    isQuickSale?: boolean
    /** Replaces the default trigger text, e.g. "Phát hành" on the pending card. */
    triggerLabel?: string
    /** The invoice never got an answer from MISA: reviewing it ends in sending it
     *  again under the same reference, after the person confirms. */
    republish?: boolean
    /** Render the trigger as a small text link instead of a button. */
    triggerLink?: boolean
}) {
    // An unanswered invoice sent again is reviewed and confirmed, not edited:
    // MISA may already hold it under another reference.
    const resend = republish && status === 'publish_unknown'
    const openManualIssue = useOpenManualIssue()
    const [editing, setEditing] = useState(false)
    const saving = useRef(false)
    const [blockedToken, setBlockedToken] = useState<string | null>()
    const [open, setOpen] = useState(initiallyOpen)
    useEffect(() => {
        if (!initiallyOpen) return
        setOpen(true)
        onInitiallyOpened?.()
    }, [initiallyOpen, onInitiallyOpened])
    const [loadedUrl, setLoadedUrl] = useState<string>()
    const [publishError, setPublishError] = useState<string>()
    const [consumed, setConsumed] = useState(false)
    const [refreshing, setRefreshing] = useState(false)
    const document = useEInvoiceDocument(orderId, open, status)
    const draft =
        [
            'draft',
            'pushed',
            'failed',
            'publish_failed',
            ...(republish ? ['publish_unknown'] : []),
        ].includes(status) &&
        (!document.observation ||
            (['draft', 'failed'].includes(document.observation.publication) &&
                document.observation.document_kind === 'saved_draft'))
    useEffect(() => {
        setEditing(false)
        setLoadedUrl(undefined)
        setPublishError(undefined)
    }, [orderId, draft])
    const remote = document.source === 'misa' || status === 'pushed'
    const regenerate: RefreshEInvoiceDraft = async (buyer, content, reset) => {
        if (saving.current || !onRefreshDraft) return
        saving.current = true
        setRefreshing(true)
        setLoadedUrl(undefined)
        setConsumed(true)
        setBlockedToken(document.previewToken)
        try {
            await onRefreshDraft(buyer, content, reset)
            document.retry()
            setEditing(false)
            setConsumed(false)
            setPublishError(undefined)
        } catch (error) {
            setPublishError(
                (error instanceof ApiError &&
                    resolveApiErrorMessage(error.code, error.params)) ||
                    (error instanceof Error
                        ? error.message
                        : m.einvoice_buyer_save_error()),
            )
            throw error
        } finally {
            saving.current = false
            setRefreshing(false)
        }
    }
    return (
        <Dialog
            open={open}
            onOpenChange={(value) => {
                if (saving.current) return
                setOpen(value)
                setEditing(false)
                setBlockedToken(undefined)
                setLoadedUrl(undefined)
                setConsumed(false)
                setPublishError(undefined)
            }}
        >
            <DialogTrigger asChild>
                <Button
                    size="sm"
                    variant={
                        triggerLink
                            ? 'link'
                            : canMutate && draft && !resend
                              ? 'default'
                              : 'outline'
                    }
                    className={triggerLink ? 'h-auto p-0 text-xs' : undefined}
                >
                    {triggerLink ? null : <FileText className="size-3.5" />}
                    {triggerLabel ??
                        (draft
                            ? m.einvoice_preview()
                            : m.einvoice_view_invoice())}
                </Button>
            </DialogTrigger>
            <DialogContent
                className={
                    editing
                        ? twJoin(
                              'flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-4xl',
                              'flex-col gap-0 overflow-hidden p-0',
                          )
                        : 'h-[90dvh] w-[96vw] max-w-6xl grid-rows-[auto_1fr_auto] p-4'
                }
            >
                <DialogHeader
                    className={
                        editing
                            ? 'shrink-0 border-b px-5 py-5 pr-10 sm:px-6 sm:pr-10'
                            : 'pr-6'
                    }
                >
                    <DialogTitle>
                        {editing
                            ? m.einvoice_buyer_edit()
                            : m.einvoice_viewer_title()}
                    </DialogTitle>
                    <DialogDescription>
                        {editing
                            ? m.einvoice_buyer_edit_hint()
                            : document.source === 'local'
                              ? m.einvoice_viewer_local_draft_hint()
                              : document.source === 'misa'
                                ? m.einvoice_viewer_misa_hint()
                                : m.einvoice_title()}
                        {!editing &&
                            !document.observation &&
                            document.status && (
                                <>
                                    {' '}
                                    {m.einvoice_viewer_status({
                                        status: statusLabel(document.status),
                                    })}
                                </>
                            )}
                    </DialogDescription>
                    {!editing && document.observation && (
                        <EInvoiceObservationNotice
                            observation={document.observation}
                        />
                    )}
                    {draft && !editing && (
                        <div className="space-y-1 rounded-md bg-muted p-3 text-left text-sm">
                            <p className="font-medium">
                                {m.einvoice_review_title()}
                            </p>
                            <p className="text-muted-foreground">
                                {m.einvoice_review_checklist()}
                            </p>
                            {canMutate &&
                                orderStatus &&
                                !orderHandedOver(orderStatus, isQuickSale) &&
                                !['cancelled', 'returned'].includes(
                                    orderStatus,
                                ) && (
                                    <p className="font-medium">
                                        {m.einvoice_delivery_hint()}
                                    </p>
                                )}
                        </div>
                    )}
                </DialogHeader>
                {editing ? (
                    <div className="flex min-h-0 flex-col overflow-hidden">
                        <EInvoiceDraftEditor
                            orderId={orderId}
                            onSave={regenerate}
                            onCancel={() => setEditing(false)}
                        />
                    </div>
                ) : document.stillSigning ? (
                    <div className="flex flex-col items-center justify-center gap-3">
                        <p
                            role="status"
                            className="text-sm text-muted-foreground"
                        >
                            {m.einvoice_document_still_signing()}
                        </p>
                        <Button variant="outline" onClick={document.retry}>
                            {m.einvoice_viewer_retry()}
                        </Button>
                    </div>
                ) : document.error ? (
                    <div className="flex flex-col items-center justify-center gap-3">
                        <p role="alert">{document.error}</p>
                        <Button variant="outline" onClick={document.retry}>
                            {m.einvoice_viewer_retry()}
                        </Button>
                    </div>
                ) : document.url ? (
                    <iframe
                        onLoad={() => setLoadedUrl(document.url)}
                        title={m.einvoice_viewer_title()}
                        src={document.url}
                        className="size-full min-h-0 rounded border"
                    />
                ) : (
                    <div
                        role="status"
                        className="flex items-center justify-center gap-2"
                    >
                        <Loader2 className="size-4 animate-spin" />
                        {m.einvoice_viewer_loading()}
                    </div>
                )}
                {!editing && (
                    <div className="space-y-2">
                        {publishError && (
                            <p
                                role="alert"
                                className="text-sm text-destructive"
                            >
                                {publishError}
                            </p>
                        )}
                        {canMutate && draft && !document.previewToken && (
                            <p className="text-sm text-muted-foreground">
                                {m.einvoice_refresh_draft_hint()}
                            </p>
                        )}
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                            <div className="space-y-1">
                                {canMutate &&
                                    draft &&
                                    onRefreshDraft &&
                                    !resend && (
                                        <>
                                            {!remote &&
                                                document.source === 'local' && (
                                                    <Button
                                                        variant="outline"
                                                        disabled={refreshing}
                                                        onClick={() =>
                                                            setEditing(true)
                                                        }
                                                    >
                                                        {m.einvoice_buyer_edit()}
                                                    </Button>
                                                )}
                                            {(remote ||
                                                !document.previewToken ||
                                                publishError) && (
                                                <Button
                                                    variant="outline"
                                                    disabled={refreshing}
                                                    onClick={() =>
                                                        void regenerate().catch(
                                                            (error: unknown) =>
                                                                setPublishError(
                                                                    error instanceof
                                                                        Error
                                                                        ? error.message
                                                                        : m.einvoice_buyer_save_error(),
                                                                ),
                                                        )
                                                    }
                                                >
                                                    {remote
                                                        ? m.einvoice_reload_remote_draft()
                                                        : m.einvoice_refresh_draft()}
                                                </Button>
                                            )}
                                            <p className="max-w-sm text-xs text-muted-foreground">
                                                {remote
                                                    ? m.einvoice_buyer_remote_hint()
                                                    : m.einvoice_buyer_local_hint()}
                                            </p>
                                        </>
                                    )}
                            </div>
                            <div className="space-y-1 sm:text-right">
                                {canMutate &&
                                    draft &&
                                    onPublish &&
                                    document.previewToken &&
                                    document.url &&
                                    loadedUrl === document.url &&
                                    !consumed &&
                                    document.previewToken !== blockedToken &&
                                    !refreshing && (
                                        <EInvoicePublishConfirm
                                            key={document.previewToken}
                                            token={document.previewToken}
                                            onPublish={onPublish}
                                            requireManualCheck={resend}
                                            orderId={orderId}
                                            onUseCandidate={
                                                openManualIssue
                                                    ? (candidate) => {
                                                          setOpen(false)
                                                          openManualIssue(
                                                              candidate,
                                                          )
                                                      }
                                                    : undefined
                                            }
                                            orderCode={orderCode}
                                            onComplete={(error) => {
                                                setConsumed(true)
                                                setPublishError(error)
                                                if (!error) setOpen(false)
                                            }}
                                        />
                                    )}
                                {canMutate && draft && onPublish && (
                                    <p className="max-w-sm text-xs text-muted-foreground">
                                        {m.einvoice_publish_next_hint()}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
